/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { LangValue } from '../../../states/types/common'

import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'

import {
  format,
  sevenDaysAgo,
  toDate,
  nowDate,
  dateManipulateDay,
  yesterDay,
  combineYearMonthAndDay,
  getYear,
  getMonth,
  correctionDate,
  prevMonth,
  nextMonth,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subtractMonth,
} from '../../../utils/date'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateRange } from 'react-date-range'
import { mapAllowedDate } from '../../../utils/plan'
import { updateCurrentDate, updateCurrentMonth } from '../../../states/containers/common/currentSlice'
import { ja, enUS } from 'date-fns/locale'

interface Props {
  changeDateCallback: (value: any) => void
  changeMonthCallback: (value: any) => void
  changeRangeCallback: (value: any) => void
}

type RangeType = 'daily' | 'monthly' | 'custom'

const RangeBlock: FC<Props> = (props) => {
  const {
    commonPart,
    current,
  } = useSelector((state: RootState) => state)

  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { changeDateCallback, changeMonthCallback, changeRangeCallback } = props
  const { language } = useParams<{ language: LangValue }>()

  const [restrictionModalShow, setRestrictionModalShow] = useState(false)

  const [targetDate, setTargetDate] = useState<string | undefined>("")
  const [targetYear, setTargetYear] = useState<number>(0)
  const [targetMonth, setTargetMonth] = useState<string>("")
  const [customStartDate, setCustomStartDate] = useState<string | undefined>("")
  const [customEndDate, setCustomEndDate] = useState<string | undefined>("")

  const [selectRange, setSelectRange] = useState<RangeType | null>("daily")
  const [isCalendarDisplay, setIsCalendarDisplay] = useState(false)

  const [isClickable, setIsClickable] = useState(true)

  const openCalendarModal = () => {
    setIsCalendarDisplay(true)
  }

  const closeCalendarModal = () => {
    setIsCalendarDisplay(false)
    setSelectRange(current.reportRangeType)
    setTargetYear(Number(combineYearMonthAndDay(current.targetMonth, 1, 'YYYY')))
  }

  const clickRangeType = (type: RangeType) => {
    if (type === 'daily' && changeDateCallback) {
      changeDateCallback(targetDate)
    } else if (type === 'monthly' && changeMonthCallback) {
      changeMonthCallback(targetMonth)
    } else if (type === 'custom' && changeRangeCallback) {
      changeRangeCallback({ startDate: customStartDate, endDate: customEndDate })
    }
    closeCalendarModal()
  }

  const handleChangeDate = (value: any) => {
    if (!commonPart.company || !changeDateCallback) return
    let selectDate = format(value.selection.startDate, 'YYYY-MM-DD')
    if (current.targetDate === selectDate) {
      selectDate = format(value.selection.endDate, 'YYYY-MM-DD')
    }
    changeDateCallback(selectDate)
    dispatch(updateCurrentDate(mapAllowedDate(selectDate, commonPart.company.plan, commonPart.company.created_at)))
    setIsCalendarDisplay(false)
    setTargetYear(Number(combineYearMonthAndDay(current.targetMonth, 1, 'YYYY')))
    setIsClickable(false)
  }

  const handleChangeMonth = (value: number) => {
    if (!changeMonthCallback) return
    changeMonthCallback(String(value))
    dispatch(updateCurrentMonth(String(value)))
    setIsCalendarDisplay(false)
    setTargetYear(Number(combineYearMonthAndDay(current.targetMonth, 1, 'YYYY')))
    setIsClickable(false)
  }

  const handleChangeRange = (value: any) => {
    if (!changeRangeCallback) return
    const dateValue = {
      startDate: format(value.selection.startDate, 'YYYY-MM-DD'),
      endDate: format(value.selection.endDate, 'YYYY-MM-DD'),
    }
    changeRangeCallback(dateValue)
    setCustomStartDate(dateValue.startDate)
    setCustomEndDate(dateValue.endDate)
    if (dateValue.startDate !== dateValue.endDate) {
      setTimeout(() => {
        setIsCalendarDisplay(false)
        setTargetYear(Number(combineYearMonthAndDay(current.targetMonth, 1, 'YYYY')))
        setIsClickable(false)
      }, 300)
    }
  }

  const changeDateRange = (value: any) => {
    const dateValue = {
      startDate: format(value.selection.startDate, 'YYYY-MM-DD'),
      endDate: format(value.selection.endDate, 'YYYY-MM-DD'),
    }
    setCustomStartDate(dateValue.startDate)
    setCustomEndDate(dateValue.endDate)
  }

  const handleSelectRange = (type: string | null) => {
    if (type === 'daily') {
      handleChangeDate(
        {
          selection: {
            startDate: targetDate,
            endDate: targetDate,
          },
        },
      )
    } else if (type === 'monthly') {
      const month = Number(targetMonth.slice(-2))
      handleChangeMonth(month)
    } else {
      handleChangeRange(
        {
          selection: {
            startDate: customStartDate,
            endDate: customEndDate,
          },
        },
      )
    }
  }

  useEffect(() => {
    if (current.targetDate) {
      setTargetDate(format(current.targetDate, 'YYYY-MM-DD'))
    } else {
      setTargetDate(nowDate('YYYY-MM-DD'))
    }
    if (current.targetMonth) {
      setTargetMonth(current.targetMonth)
      setTargetYear(Number(combineYearMonthAndDay(current.targetMonth, 1, 'YYYY')))
    } else {
      setTargetMonth(String(getYear()) + (`0${String(getMonth())}`).slice(-2))
      setTargetYear(getYear())
    }
    if (current.targetStartDate) {
      setCustomStartDate(format(current.targetStartDate, 'YYYY-MM-DD'))
    } else {
      setCustomStartDate(nowDate('YYYY-MM-DD'))
    }
    if (current.targetEndDate) {
      setCustomEndDate(format(current.targetEndDate, 'YYYY-MM-DD'))
    } else {
      setCustomEndDate(nowDate('YYYY-MM-DD'))
    }
    if (current.reportRangeType) {
      setSelectRange(current.reportRangeType)
    } else {
      setSelectRange('daily')
    }
  }, [current])

  useEffect(() => {
    setTimeout(() => {
      setIsClickable(true)
    }, 3000)
  }, [isClickable])

  const renderCalender = (type: string | null) => {
    if (type === 'daily') {
      return (
        <>
          <DateRange
            locale={language === 'en' ? enUS : ja}
            ranges={[{ startDate: toDate(targetDate), endDate: toDate(targetDate), key: 'selection' }]}
            onChange={(value) => handleChangeDate(value)}
            maxDate={toDate(nowDate('YYYY-MM-DD'))}
            minDate={toDate(commonPart.company?.created_at)}
            showDateDisplay={false}
            dragSelectionEnabled={false}
            showPreview={false}
            moveRangeOnFirstSelection
            weekStartsOn={0}
          />
        </>
      )
    }
    if (type === 'monthly') {
      const month = t('time.date.formats.monthName').split(' ').map((v) => v)
      return (
        <>
          <div className="select_month">
            <div>
              <button
                type="button"
                onClick={() => setTargetYear(targetYear - 1)}
                disabled={Number(format(commonPart.company?.created_at, 'YYYY')) >= targetYear}
              >
                <FontAwesomeIcon icon={['fas', 'angle-left']} className="icon" />
              </button>
            </div>
            <div>
              {`${targetYear}${t('time.time.short.year')}`}
            </div>
            <div>
              <button
                type="button"
                onClick={() => setTargetYear(targetYear + 1)}
                disabled={getYear() <= targetYear}
              >
                <FontAwesomeIcon icon={['fas', 'angle-right']} className="icon" />
              </button>
            </div>
          </div>
          {
            month.map((v, i) => {
              const target = String(targetYear) + (`0${String(i + 1)}`).slice(-2)
              const isClickableMonth = Number(nowDate('YYYYMM')) >= Number(target)
                && Number(format(commonPart.company?.created_at, 'YYYYMM')) <= Number(target)
              const isSelected = current.targetMonth === target
              return (
                <div className="monthly_button" key={v}>
                  <button
                    type="button"
                    className={isSelected ? "selected" : ""}
                    onClick={() => handleChangeMonth(Number(target))}
                    disabled={!isClickableMonth}
                  >
                    {`${v}${t('time.time.short.month')}`}
                  </button>
                </div>
              )
            })
          }
        </>
      )
    }
    return (
      <DateRange
        months={2}
        direction="horizontal"
        locale={language === 'en' ? enUS : ja}
        weekStartsOn={0}
        ranges={[{ startDate: toDate(customStartDate), endDate: toDate(customEndDate), key: 'selection' }]}
        onChange={(value) => changeDateRange(value)}
        maxDate={toDate(yesterDay('YYYY-MM-DD'))}
        minDate={toDate(commonPart.company?.created_at)}
        dateDisplayFormat={language === 'en' ? "eee, MMMM do, yyyy" : "yyyy年M月d日 (eee)"}
        monthDisplayFormat={language === 'en' ? "MMMM yyyy" : "yyyy年MM月"}
        showDateDisplay={false}
      />
    )
  }

  const renderQuick = (
    <>
      <button
        type="button"
        className="button"
        onClick={() => handleChangeRange(
          {
            selection: {
              startDate: correctionDate(format(startOfWeek(dateManipulateDay(nowDate(), -7)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
              endDate: correctionDate(format(endOfWeek(dateManipulateDay(nowDate(), -7)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
            },
          },
        )}
      >
        {t('range.calendar.quick.custom.type_1')}
      </button>
      <button
        type="button"
        className="button"
        onClick={() => handleChangeRange(
          {
            selection: {
              startDate: correctionDate(format(startOfWeek(dateManipulateDay(nowDate(), -14)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
              endDate: correctionDate(format(endOfWeek(dateManipulateDay(nowDate(), -7)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
            },
          },
        )}
      >
        {t('range.calendar.quick.custom.type_2')}
      </button>
      <button
        type="button"
        className="button"
        onClick={() => handleChangeRange(
          {
            selection: {
              startDate: correctionDate(format(startOfWeek(dateManipulateDay(nowDate(), -21)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
              endDate: correctionDate(format(endOfWeek(dateManipulateDay(nowDate(), -7)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
            },
          },
        )}
      >
        {t('range.calendar.quick.custom.type_3')}
      </button>
      <button
        type="button"
        className="button"
        onClick={() => handleChangeRange(
          {
            selection: {
              startDate: correctionDate(format(startOfMonth(subtractMonth(nowDate(), 1)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
              endDate: correctionDate(format(endOfMonth(subtractMonth(nowDate(), 1)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
            },
          },
        )}
      >
        {t('range.calendar.quick.custom.type_4')}
      </button>
      <button
        type="button"
        className="button"
        onClick={() => handleChangeRange(
          {
            selection: {
              startDate: correctionDate(format(startOfMonth(subtractMonth(nowDate(), 3)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
              endDate: correctionDate(format(endOfMonth(subtractMonth(nowDate(), 1)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
            },
          },
        )}
      >
        {t('range.calendar.quick.custom.type_5')}
      </button>
      <button
        type="button"
        className="button"
        onClick={() => handleChangeRange(
          {
            selection: {
              startDate: correctionDate(format(startOfMonth(subtractMonth(nowDate(), 6)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
              endDate: correctionDate(format(endOfMonth(subtractMonth(nowDate(), 1)), 'YYYY-MM-DD'), 'YYYY-MM-DD'),
            },
          },
        )}
      >
        {t('range.calendar.quick.custom.type_6')}
      </button>
    </>
  )

  const renderDate = (type: string | null) => {
    if (type === 'daily' && targetDate) {
      return (
        <>
          <button
            type="button"
            className="display button"
            onClick={() => openCalendarModal()}
            disabled={!isClickable}
          >
            {format(current.targetDate, t('time.date.formats.with_weekday'), language)}
            <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
          </button>
          <button
            type="button"
            className="prev button"
            onClick={
              () => handleChangeDate(
                {
                  selection: {
                    startDate: dateManipulateDay(current.targetDate, -1, 'YYYY-MM-DD'),
                    endDate: dateManipulateDay(current.targetDate, -1, 'YYYY-MM-DD'),
                  },
                },
              )
            }
            disabled={
              format(current.targetDate) === format(commonPart.company?.created_at)
              || !isClickable
            }
          >
            <FontAwesomeIcon icon={['fas', 'angle-left']} className="icon" />
          </button>
          <button
            type="button"
            className="next button"
            onClick={
              () => handleChangeDate(
                {
                  selection: {
                    startDate: dateManipulateDay(current.targetDate, 1, 'YYYY-MM-DD'),
                    endDate: dateManipulateDay(current.targetDate, 1, 'YYYY-MM-DD'),
                  },
                },
              )
            }
            disabled={format(current.targetDate) === nowDate() || !isClickable}
          >
            <FontAwesomeIcon icon={['fas', 'angle-right']} className="icon" />
          </button>
        </>
      )
    }
    if (type === 'monthly' && targetMonth) {
      return (
        <>
          <button
            type="button"
            className="display button"
            onClick={() => openCalendarModal()}
            disabled={!isClickable}
          >
            {combineYearMonthAndDay(current.targetMonth, 1, t('time.date.formats.month_year'), language)}
            <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
          </button>
          <button
            type="button"
            className="prev button"
            onClick={
              () => handleChangeMonth(
                Number(prevMonth(`${current.targetMonth.substring(0, 4)}-${current.targetMonth.substring(4)}-01`, 'YYYYMM')),
              )
            }
            disabled={current.targetMonth === format(commonPart.company?.created_at, "YYYYMM") || !isClickable}
          >
            <FontAwesomeIcon icon={['fas', 'angle-left']} className="icon" />
          </button>
          <button
            type="button"
            className="next button"
            onClick={
              () => handleChangeMonth(
                Number(nextMonth(`${current.targetMonth.substring(0, 4)}-${current.targetMonth.substring(4)}-01`, 'YYYYMM')),
              )
            }
            disabled={current.targetMonth === nowDate("YYYYMM") || !isClickable}
          >
            <FontAwesomeIcon icon={['fas', 'angle-right']} className="icon" />
          </button>
        </>
      )
    }
    if (type === 'custom' && customStartDate && customEndDate) {
      return (
        <button
          type="button"
          className="display button"
          onClick={() => openCalendarModal()}
          disabled={!isClickable}
        >
          {`${format(customStartDate, t('time.date.formats.with_weekday'), language)} 
          ~ ${customStartDate === customEndDate ? t('time.date.none') : format(customEndDate, t('time.date.formats.with_weekday'), language)}`}
          <FontAwesomeIcon icon={['fas', 'angle-down']} className={`icon ${isCalendarDisplay && 'open'}`} />
        </button>
      )
    }
    return ""
  }

  return (
    <>
      <div className="tools">
        <div className="calendar_text">
          {
            current.reportRangeType && (
              renderDate(current.reportRangeType)
            )
          }
          {
            targetDate && (
              <div className={`calendar_box ${isCalendarDisplay || 'none'}`}>
                <div className="range">
                  <div className="text">
                    {t(`range.calendar.header_text.${selectRange}`)}
                  </div>
                </div>
                <div className={selectRange || ""}>
                  {renderCalender(selectRange)}
                </div>
                {
                  selectRange === 'custom' && (
                    <>
                      <div className="calendar_quick">
                        <div className="text">
                          {t('range.calendar.quick.title')}
                        </div>
                        <div className="field">
                          {renderQuick}
                        </div>
                      </div>
                      <div className="calendar_button_area">
                        <button
                          type="button"
                          className="cancel_button"
                          onClick={closeCalendarModal}
                        >
                          {t('range.cancel')}
                        </button>
                        <button
                          type="button"
                          className="submit_button"
                          onClick={() => handleSelectRange('custom')}
                        >
                          {t('range.submit')}
                        </button>
                      </div>
                    </>
                  )
                }
              </div>
            )
          }
        </div>
        {
          targetDate && (
            <div className="calendar_range">
              <button
                type="button"
                className={selectRange === "daily" ? "selected" : ""}
                onClick={() => clickRangeType("daily")}
                disabled={!isClickable || selectRange === "daily"}
              >
                {t('range.daily')}
              </button>
              <button
                type="button"
                className={selectRange === "monthly" ? "selected" : ""}
                onClick={() => clickRangeType("monthly")}
                disabled={!isClickable || selectRange === "monthly"}
              >
                {t('range.monthly')}
              </button>
              <button
                type="button"
                className={selectRange === "custom" ? "selected" : ""}
                onClick={() => clickRangeType("custom")}
                disabled={!isClickable || selectRange === "custom"}
              >
                {t('range.custom')}
              </button>
            </div>
          )
        }
        <div className={`screen transparent ${isCalendarDisplay || 'none'}`} role="presentation" onClick={closeCalendarModal} />
      </div>
    </>
  )
}

export default RangeBlock
